import { Ibounding } from './interfaces';
export function format(first: string, middle: string, last: string): string {
  if (!first && !middle && !last) {
    return '';
  }
  if (first && middle && last) {
    return `${first} ${middle} ${last}`;
  }
  if (first && middle) {
    return `${first} ${middle}`;
  }
  if (first && last) {
    return `${first} ${last}`;
  }
  if (middle && last) {
    return `${middle} ${last}`;
  }
  if (first) {
    return first;
  }
  if (middle) {
    return middle;
  }
  if (last) {
    return last;
  }
}

export const makeScale = (in_min: number, in_max: number, out_min: number, out_max: number) => {
  return (num: number) => ((num - in_min) * (out_max - out_min)) / (in_max - in_min) + out_min;
};

export function hasShadowDom(el: HTMLElement) {
  return !!el.shadowRoot && !!(el as any).attachShadow;
}

export const delay = (delay: number, value: any = true) => new Promise(resolve => setTimeout(resolve, delay, value));

export const reqIdleCallback = (callback: () => void) => {
  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(callback);
  } else {
    setTimeout(callback, 32);
  }
};

export const matchMedia = (win: Window, query: string): boolean => win.matchMedia(query).matches;

export const isStr = (val: any): val is string => typeof val === 'string';

export const toLower = (val: string) => val.toLocaleLowerCase('en-US');

export const urlFriendly = (str: string) => toLower(str).replace(/\s+/g, '-');

export const getKeyboardFocusableElements = (element: any) => {
  return [...Array.from(element?.querySelectorAll('a, button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])') as ArrayLike<HTMLElement>)].filter(
    el => !el.hasAttribute('disabled'),
  );
};

export const swapTabIndexAndFocus = (current: HTMLElement, toFocus: HTMLElement) => {
  current.setAttribute('tabindex', '-1');
  toFocus.setAttribute('tabindex', '0');
  toFocus.focus();
};

export const isOutOfViewport = (elem: HTMLElement) => {
  // Get element's bounding
  let bounding = elem.getBoundingClientRect();

  // Check if it's out of the viewport on each side
  let out = {} as Ibounding;
  out.top = bounding.top <= 0;
  out.left = bounding.left <= 0;
  out.bottom = bounding.bottom >= (window.innerHeight || document.documentElement.clientHeight);
  out.right = bounding.right >= (window.innerWidth || document.documentElement.clientWidth);
  out.any = out.top || out.left || out.bottom || out.right;
  out.all = out.top && out.left && out.bottom && out.right;
  out.values = bounding;

  return out;
};

export const isVisible = (elem: HTMLElement, container: HTMLElement, direction: 'x' | 'y') => {
  const { x, y } = elem.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  return direction === 'x' ? x > containerRect.width : y < containerRect.height;
};

/**
 * @param {HTMLElement} element - A focusable HTML element that has an explicit tabindex set.
 * @description - Simple function to remove tabindex from a given element (usually "-1").
 * Should be applied to elements that are able to receive focus.
 */
export const enableFocus = (element: HTMLElement) => {
  element.removeAttribute('aria-hidden');
  element.removeAttribute('tabindex');
};
/**
 * @param {HTMLElement} element - A focusable HTML element that will receive an explicit tabindex="-1" attribute.
 * @description - Simple function to disable keyboard focus on a given element.
 * Should be applied to elements that are able to receive focus.
 */
export const disableFocus = (element: HTMLElement) => {
  element.setAttribute('aria-hidden', 'true');
  element.setAttribute('tabindex', '-1');
};

// METRICS - COMPONENTS
export const metrics = (componentName: string, _props?: any) => {
  let detail: {
    tag: string;
    // props: { [key: string]: string };
  } = {
    tag: componentName,
    // props: {},
  };

  // for (const property in props) {
  //   detail.props[property] = props[property] as any;
  // }

  const track = new CustomEvent('prism-component-connected', {
    detail,
  });
  document.dispatchEvent(track);
};
// https://share.goabstract.com/b244c1ca-d39f-4169-95ba-ecaedcaa2775?collectionLayerId=443ab909-da04-45c4-95bb-089caf777608&mode=design
export const iconDimensions = {
  xs: { w: 16, h: 16 },
  sm: { w: 24, h: 24 },
  md: { w: 32, h: 32 },
  lg: { w: 48, h: 48 },
  xl: { w: 72, h: 72 },
  custom: { w: 0, h: 0 },
};

// UUID Generation
export const getGUID = () => {
  return crypto.randomUUID();
};

// Check if items from array exist in a string
export const checkWords = (collection: Array<string>, value: string) => {
  return collection.some(word => value.includes(word));
};

// Capitialize First Letter
export const capitalizeFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

// Takes input string and checks against array of charecters and removes those characters from the string.
export function removeSpecialChars(input: string, specialChars: Array<string>) {
  let output = '';
  for (let i = 0; i < input.length; i++) {
    if (!specialChars.includes(input[i])) {
      output += input[i];
    }
  }
  return output;
}
const outputConsole = (component: string, message: string, noticePrefix: string) => {
  let consoleStylesPrefix = 'font-size:12px; font-weight:bold; color:rgb(20, 20, 23); padding:2px; background-color:rgb(217, 225, 242);';
  let consoleStylesMessageStart = 'font-size:12px; margin-left:4px; padding:2px;';
  let consoleStylesComponentName = 'font-size:12px; font-weight:bolder;';
  let consoleStylesMessageEnd = 'font-size:12px; padding:2px;';
  console.info(
    `%c💎 Prism UI Component - ${noticePrefix}:%cThe %c${component} %c${message}`,
    consoleStylesPrefix,
    consoleStylesMessageStart,
    consoleStylesComponentName,
    consoleStylesMessageEnd,
  );
};
// Deprecation Notice Function
export const deprecationNotice = (component: string, message: string) => {
  if (typeof Storage !== 'undefined' && JSON.parse(localStorage.getItem('@prism-ui/settings'))?.notices) {
    outputConsole(component, message, 'Deprecation Notice');
  }
};

// Prism 8 Supported Exports Utils

// eslint-disable-next-line @stencil-community/ban-exported-const-enums
export enum cdnpaths {
  xd = 'https://static.cimcontent.net/common-web-assets/xds_icons/svg/',
  illicons = 'https://static.cimcontent.net/common-web-assets/illicons/',
  business = 'https://content.ecom.digital.business.comcast.com/images/icons/gds/',
}
